/**
 * Rive layer for the FishPrawnCrab benchmark port.
 *
 * Three canvases — mirroring the three `RiveWidgetController`s in
 *   packages/amgfishprawncrab_v2/lib/presentation/rive/rive_controller_manager.dart:
 *
 *     main    → FishPrawnCrab.riv  artboard "Main"               (background, girl, history, min/max text)
 *     topbowl → FishPrawnCrab.riv  artboard "topBowlAnimation"   (dice bowl + dice reveal)
 *     betting → bettingui.riv      artboard "Main"               (interactive bet zones, chip picker)
 *
 * Why one Rive instance per canvas: `@rive-app/webgl2` clears the canvas at
 * the start of every frame, so two instances sharing a canvas overwrite each
 * other.
 *
 * Inputs are cached by name AND queried by `StateMachineInputType`. Nested
 * inputs (e.g. `result` at path `Historyicon3`) go through
 * `setNumberStateAtPath`.
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import { FpcInputs } from './riveInputs';

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

interface RiveTarget {
  key: TargetKey;
  file: string;
  stateMachine: string;
  instance: Rive;
  inputs: Map<string, AnyInput>;
}

export type TargetKey = 'main' | 'topbowl' | 'betting';

const FILES: Record<TargetKey, string> = {
  main: 'FishPrawnCrab.riv',
  topbowl: 'FishPrawnCrab.riv',
  betting: 'bettingui.riv',
};

const ARTBOARDS: Record<TargetKey, string> = {
  main: 'Main',
  topbowl: 'topBowlAnimation',
  betting: 'Main',
};

const STATE_MACHINES: Record<TargetKey, string> = {
  main: FpcInputs.stateMachine,
  topbowl: FpcInputs.stateMachine,
  betting: FpcInputs.stateMachine,
};

const LAYOUT_FIT: Record<TargetKey, Fit> = {
  main: Fit.Cover,
  topbowl: Fit.Cover,
  betting: Fit.Contain,
};

export interface RiveLayerOptions {
  canvases: Record<TargetKey, HTMLCanvasElement>;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  | 'placeBetClick'
  | 'evtClickChips'
  | 'evtClearClick'
  | 'evtButtonPlayClick'
  | 'evtSoundOnOff'
  | 'evtMusicOnOff'
  | 'UIButtonClick'
  | 'DiceBowlEnd';

type RiveEventCallback = (data: Record<string, unknown>) => void;

export class RiveLayer {
  private readonly canvases: Record<TargetKey, HTMLCanvasElement>;
  private readonly assetBase: string;
  private readonly targets: Map<TargetKey, RiveTarget> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();

  constructor(opts: RiveLayerOptions) {
    this.canvases = opts.canvases;
    this.assetBase = opts.assetBase ?? 'assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitAllCanvases();
    window.addEventListener('resize', () => this.fitAllCanvases());

    await Promise.all([
      this.loadOne('main'),
      this.loadOne('topbowl'),
      this.loadOne('betting'),
    ]);

    this.dumpInputs();
    this.emit('ready', {});
  }

  private fitAllCanvases(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of Object.values(this.canvases)) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }
    for (const t of this.targets.values()) {
      try {
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  private loadOne(key: TargetKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = FILES[key];
      const artboard = ARTBOARDS[key];
      const sm = STATE_MACHINES[key];
      const canvas = this.canvases[key];
      const src = `${this.assetBase}${file}`;
      const rive = new Rive({
        canvas,
        src,
        artboard,
        stateMachines: sm,
        autoplay: true,
        layout: new Layout({ fit: LAYOUT_FIT[key], alignment: Alignment.Center }),
        onLoad: () => {
          try {
            const inputs = this.cacheInputs(rive, sm);
            const target: RiveTarget = {
              key,
              file,
              stateMachine: sm,
              instance: rive,
              inputs,
            };
            this.targets.set(key, target);
            this.attachEventBridge(rive, key);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(new Error(`Failed to load ${src} [${artboard}/${sm}]: ${String(err)}`));
        },
      });
    });
  }

  private cacheInputs(rive: Rive, sm: string): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    let list: unknown[] = [];
    try {
      list = rive.stateMachineInputs(sm) ?? [];
    } catch (e) {
      warn('[Rive] stateMachineInputs failed for', sm, e);
      return out;
    }
    for (const raw of list) {
      const inp = raw as {
        name: string;
        type: number;
        value: number | boolean;
        fire(): void;
      };
      switch (inp.type) {
        case StateMachineInputType.Number:
          out.set(inp.name, {
            type: 'number',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Number(inp.value ?? 0),
          });
          break;
        case StateMachineInputType.Boolean:
          out.set(inp.name, {
            type: 'boolean',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Boolean(inp.value),
          });
          break;
        case StateMachineInputType.Trigger:
          out.set(inp.name, {
            type: 'trigger',
            name: inp.name,
            fire: () => inp.fire(),
          });
          break;
        default:
          debugLog('[Rive] unknown input type', inp.type, 'for', inp.name);
      }
    }
    return out;
  }

  private dumpInputs(): void {
    for (const t of this.targets.values()) {
      const rows = Array.from(t.inputs.values()).map((i) => ({
        target: t.key,
        file: t.file,
        sm: t.stateMachine,
        name: i.name,
        type: i.type,
      }));
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `[Rive] ${t.key} (${t.file} / ${t.stateMachine}) — ${rows.length} input(s)`,
      );
      // eslint-disable-next-line no-console
      console.table(rows);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  private attachEventBridge(rive: Rive, target: TargetKey): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as { data?: { name?: string; properties?: Record<string, unknown> } };
        const name = e?.data?.name;
        if (!name) return;
        const props = { ...(e?.data?.properties ?? {}), __target: target };
        if (this.listeners.has(name)) {
          this.emit(name, props);
        } else {
          debugLog('[Rive][event:passthrough]', target, name, props);
        }
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  // -----------------------------------------------------------------
  // Public high-level API — mirrors RiveControllerManager
  // -----------------------------------------------------------------

  /**
   * Set the 3 dice values (1..6 each) on the topBowl artboard.
   * Mirrors setDiceResult in rive_controller_manager.dart:163-173.
   */
  setDiceResult(n1: number, n2: number, n3: number): void {
    this.setNumberAny('topbowl', FpcInputs.diceNum1, n1);
    this.setNumberAny('topbowl', FpcInputs.diceNum2, n2);
    this.setNumberAny('topbowl', FpcInputs.diceNum3, n3);
  }

  /** 1 = shaking (waiting), 2 = revealed. */
  setDiceBowlPosition(value: 1 | 2): void {
    this.setNumberAny('topbowl', FpcInputs.diceBowlPosition, value);
  }

  /** Fire the bowl-play trigger. Together with setDiceBowlPosition(1|2) drives the reveal. */
  triggerDiceBowlPlay(): void {
    this.fireTriggerAny('topbowl', FpcInputs.diceBowlPlay);
  }

  /** Combined helper: set dice + position=2 + fire — equivalent to the Flutter "_tryStartDiceBowlReveal" call sequence. */
  startDiceReveal(n1: number, n2: number, n3: number): void {
    this.setDiceResult(n1, n2, n3);
    this.setDiceBowlPosition(2);
    this.triggerDiceBowlPlay();
  }

  /** Position 1 + fire — restart the shaking idle. */
  startDiceShaking(): void {
    this.setDiceBowlPosition(1);
    this.triggerDiceBowlPlay();
  }

  /** Girl animation: 1=idle (intro/loop), 2=end-of-round outro. */
  playGirlAnimation(selection: 1 | 2): void {
    this.setNumberAny('main', FpcInputs.girlAnimSelection, selection);
    this.fireTriggerAny('main', FpcInputs.girlAnimTrigger);
  }

  setIsMobile(value: boolean): void {
    this.setBoolAny('betting', FpcInputs.isMobile, value);
  }

  setBetLimitsText(minBet: number, maxBet: number): void {
    // Mirrors rive_controller_manager.dart:74-93 — set on the main artboard's text runs.
    const minTxt = `$ ${minBet}`;
    const maxTxt = `$ ${maxBet}`;
    const main = this.targets.get('main');
    if (!main) return;
    try {
      main.instance.setTextRunValue(FpcInputs.txtMinValue, minTxt);
    } catch (e) {
      debugLog('[Rive] setText txtMinValue failed', e);
    }
    try {
      main.instance.setTextRunValue(FpcInputs.txtMaxValue, maxTxt);
    } catch (e) {
      debugLog('[Rive] setText txtMaxValue failed', e);
    }
  }

  /**
   * Set the history icon at slot index (1-based, 1..10).
   * Nested input `result` at path `Historyicon{index}` on the main artboard.
   */
  setHistorySlot(index1Based: number, value: number): void {
    if (index1Based < 1 || index1Based > 10) return;
    const path = `${FpcInputs.historyPathPrefix}${index1Based}`;
    const main = this.targets.get('main');
    if (!main) return;
    try {
      main.instance.setNumberStateAtPath(FpcInputs.nestedHistoryResult, value, path);
    } catch (e) {
      debugLog(`[Rive] setNumberStateAtPath('${FpcInputs.nestedHistoryResult}', ${value}, '${path}') failed:`, e);
    }
  }

  setAllHistory(values: ReadonlyArray<number>, max = 10): void {
    for (let i = 1; i <= max; i++) this.setHistorySlot(i, 0);
    for (let i = 0; i < values.length && i < max; i++) {
      this.setHistorySlot(i + 1, values[i]);
    }
  }

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  on(topic: RiveEventTopic | string, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // -----------------------------------------------------------------
  // Low-level input plumbing
  // -----------------------------------------------------------------

  private setNumberAny(key: TargetKey, name: string, value: number): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not number`);
      return false;
    }
    debugLog(`[Rive] number '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private setBoolAny(key: TargetKey, name: string, value: boolean): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'boolean') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not boolean`);
      return false;
    }
    debugLog(`[Rive] bool '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private fireTriggerAny(key: TargetKey, name: string): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'trigger') {
      inp.fire();
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not trigger`);
      return false;
    }
    debugLog(`[Rive] trigger '${name}' not found on ${key} (${t.file})`);
    return false;
  }
}
